import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { FiUser, FiChevronDown, FiLogOut, FiCheck, FiSettings } from 'react-icons/fi';
import { RootState, AppDispatch } from '../../store';
import { signOut } from '../../store/authSlice';
import { setSelectedProject } from '../../store/project/projectSlice';
import ThemeToggle from '../common/ThemeToggle';
import LanguageSelect from '../common/LanguageSelect';
import HelpMenu from './HelpMenu';
import { useTranslation } from 'react-i18next';
import UserSettingsDialog from './UserSettingsDialog';
import { usePresence } from '../../hooks/usePresence';
import { useCapabilities } from '../../services/capabilities';
import { showToast } from '../common/Toast';

export type Screen = 'dashboard' | 'runner' | 'designer';

interface AppHeaderProps {
  screen: Screen;
  onChangeScreen: (s: Screen) => void;
}

// 표시 이름은 번역 키로 — 모듈 상수에서 t() 를 부르면 언어를 바꿔도 갱신되지 않는다
const MENUS: { key: Screen; labelKey: string }[] = [
  { key: 'dashboard', labelKey: 'layout:menu.dashboard' },
  { key: 'runner', labelKey: 'layout:menu.runner' },
  { key: 'designer', labelKey: 'layout:menu.designer' },
];

/**
 * 화면 최상단 헤더 — LiveSQL 의 appHeader 구성을 따른다 (높이 50px, 12px 글꼴).
 *   왼쪽  : 제품 타이틀 + beta 표기 + 메뉴바(대시보드 / 실행 / 디자이너)
 *   가운데: 현재 프로젝트 / 워크플로 배지 (LiveSQL 의 접속 정보 배지 자리)
 *   오른쪽: 도움말 메뉴 + 테마 토글 + 사용자 메뉴(▼ 드롭다운: 프로젝트 변경, 로그아웃)
 */
const AppHeader: React.FC<AppHeaderProps> = ({ screen, onChangeScreen }) => {
  const { t } = useTranslation();
  // 관리자에게 바꿔야 할 보안 설정(기본 비밀번호·root 실행 …)을 로그인한 세션에 한 번 알린다
  const caps = useCapabilities();
  useEffect(() => {
    const w = caps?.warnings || [];
    if (!w.length) return;
    try { if (sessionStorage.getItem('openflow:securityWarned')) return; sessionStorage.setItem('openflow:securityWarned', '1'); } catch { /* 저장소를 못 써도 알린다 */ }
    showToast(`${t('layout:security.title')}\n${w.map((c) => `· ${t(`layout:security.${c}`)}`).join('\n')}`, 'warn');
  }, [caps, t]);
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const projects = useSelector((state: RootState) => state.project.projects);
  const selectedProjectId = useSelector((state: RootState) => state.project.selectedProjectId);
  const workflowName = useSelector((state: RootState) => state.workflow.current?.name);
  const project = projects.find(p => p.id === selectedProjectId);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  // 프로젝트 전환 — 로그인 직후 선택 화면은 내 것만 보이므로, 공유받은 프로젝트는 여기서 연다
  const [projMenuOpen, setProjMenuOpen] = useState(false);
  const projMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!projMenuOpen) return;
    const onDown = (e: MouseEvent) => { if (projMenuRef.current && !projMenuRef.current.contains(e.target as Node)) setProjMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [projMenuOpen]);
  const mine = projects.filter((p) => !p.relation || p.relation === 'owner');
  const shared = projects.filter((p) => p.relation && p.relation !== 'owner');
  const pick = (id: string) => { setProjMenuOpen(false); if (id !== selectedProjectId) dispatch(setSelectedProject(id)); };
  const projItem = (p: typeof projects[number]) => (
    <button key={p.id} type="button" onClick={() => pick(p.id)}
      className="w-full flex items-center gap-2 px-4 py-1.5 text-left text-gray-700 hover:bg-gray-100">
      <span className="w-3.5 shrink-0 text-blue-600">
        {p.id === selectedProjectId && (
          // @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피
          <FiCheck />
        )}
      </span>
      <span className="flex-1 min-w-0 truncate">{p.name}</span>
      {p.relation && p.relation !== 'owner' && (
        <span className="shrink-0 text-xs text-gray-500">
          {t('layout:project.sharedBy', { owner: p.ownerUsrid || '-', role: t(`layout:members.role.${p.relation}`) })}
        </span>
      )}
    </button>
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  // 메뉴는 천천히 나타나고 사라진다 — 툭 붙었다 떨어지면 급작스럽다
  const userMenu = usePresence(userMenuOpen, 160);
  const userLabel = user?.name || user?.loginId || t('layout:user.fallback');

  return (
    <header className="app-header h-[50px] shrink-0 flex items-center justify-between px-5 bg-white border-b border-gray-200 text-sm text-gray-700">
      {/* 왼쪽: 타이틀 + 메뉴바 */}
      <div className="flex items-center gap-5 shrink-0">
        <span className="flex items-center gap-1.5 font-bold text-lg text-gray-800 select-none">
          OpenFlow
          <sup className="text-[0.5625rem] font-normal text-gray-400">beta</sup>
        </span>
        <nav className="flex items-center gap-2.5">
          {MENUS.map(m => (
            <button
              key={m.key}
              type="button"
              onClick={() => onChangeScreen(m.key)}
              className={classNames(
                'px-2.5 py-1.5 rounded text-sm transition-colors',
                screen === m.key
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              {t(m.labelKey)}
            </button>
          ))}
        </nav>
      </div>

      {/* 가운데: 프로젝트 / 워크플로 배지 */}
      <div className="flex-1 flex items-center justify-center min-w-0 mx-5">
        {project && (
          <div className="relative min-w-0 max-w-full" ref={projMenuRef}>
          <button
            type="button"
            onClick={() => setProjMenuOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 font-medium whitespace-nowrap max-w-full overflow-hidden text-ellipsis hover:bg-blue-100"
            title={`${t('layout:header.projectBadge', { project: project.name })}${workflowName ? t('layout:header.workflowBadge', { workflow: workflowName }) : ''} — ${t('layout:header.switchProject')}`}
          >
            <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
            <span className="truncate">{project.name}</span>
            {workflowName && (
              <>
                <span className="text-blue-300">/</span>
                <span className="truncate text-blue-800">{workflowName}</span>
              </>
            )}
            {/* @ts-ignore */}
            <FiChevronDown className={classNames('text-xs shrink-0 transition-transform', projMenuOpen && 'rotate-180')} />
          </button>
          {projMenuOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+8px)] min-w-[280px] max-w-[420px] max-h-[60vh] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] py-1 text-sm">
              <div className="px-4 py-1 text-xs font-semibold text-gray-500">{t('layout:header.mineTitle')}</div>
              {mine.map(projItem)}
              <div className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-500 border-t border-gray-200 mt-1">{t('layout:header.sharedTitle')}</div>
              {shared.length ? shared.map(projItem) : <div className="px-4 py-1.5 text-xs text-gray-400">{t('layout:header.noShared')}</div>}
            </div>
          )}
          </div>
        )}
      </div>

      {/* 오른쪽: 도움말 + 언어 + 테마 토글 + 사용자 메뉴 */}
      <div className="flex items-center gap-3 shrink-0 whitespace-nowrap">
        <HelpMenu />
        <LanguageSelect />
        <ThemeToggle />
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen(v => !v)}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-blue-700 font-medium hover:bg-blue-50 transition-colors"
            title={t('layout:user.menu')}
          >
            {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
            <FiUser />
            <span className="font-semibold">{userLabel}</span>
            {/* @ts-ignore */}
            <FiChevronDown className={classNames('text-xs transition-transform', userMenuOpen && 'rotate-180')} />
          </button>
          {userMenu.mounted && (
            <div className={classNames('absolute right-0 top-[calc(100%+8px)] min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] overflow-hidden origin-top-right transition-all duration-150 ease-out',
              userMenu.shown ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-1 scale-95 pointer-events-none')}>
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="font-semibold text-gray-900">{userLabel}</div>
                <div className="text-xs text-gray-500 mt-0.5">{user?.email || user?.loginId}</div>
              </div>
              {/* 프로젝트 변경·내보내기·불러오기는 시스템 버튼 줄의 「프로젝트」 로 옮겼다(ProjectMenu, 2026-10-04) */}
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                onClick={() => { setUserMenuOpen(false); setSettingsOpen(true); }}
                title={t('layout:userSettings.menuTitle')}
              >
                {/* @ts-ignore */}
                <FiSettings /> {t('layout:userSettings.menu')}
              </button>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-red-600 hover:bg-gray-100 border-t border-gray-200"
                onClick={() => { setUserMenuOpen(false); dispatch(signOut()); }}
              >
                {/* @ts-ignore */}
                <FiLogOut /> {t('layout:user.signOut')}
              </button>
            </div>
          )}
        </div>
      </div>
      <UserSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </header>
  );
};

export default AppHeader;
